'use client'

import { confirm } from '@repo/ui/Confirm'
import { DataGridPro, gridClasses } from '@mui/x-data-grid-pro'
import { formatCurrency } from '@repo/utils/numbers'
import { formatDate } from '@repo/utils/time'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { useCallback, useEffect, useState } from 'react'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { useParams } from 'react-router-dom'
import MUIAlert from '@mui/material/Alert'
import MUIButton from '@mui/material/Button'
import MUIDialog from '@mui/material/Dialog'
import MUIDialogActions from '@mui/material/DialogActions'
import MUIDialogContent from '@mui/material/DialogContent'
import MUIDialogTitle from '@mui/material/DialogTitle'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIPaper from '@mui/material/Paper'
import MUIRadio from '@mui/material/Radio'
import MUIRadioGroup from '@mui/material/RadioGroup'
import MUIStack from '@mui/material/Stack'

const canConfirmMatch = (matchStatus) =>
  matchStatus === 'ProposedFuzzy' || matchStatus === 'ProposedGroup' || matchStatus === 'ProposedExact'

// ReMatchAsync's own real precondition: only Unmatched/ProposedFuzzy/ProposedExact are
// eligible, a ProposedGroup row would 409
const canReMatch = (matchStatus) =>
  matchStatus === 'Unmatched' || matchStatus === 'ProposedFuzzy' || matchStatus === 'ProposedExact'

const BankFeedsReviewQueue = () => {
  const { plaidBankAccountId } = useParams()
  const { enableBankFeeds, enableAutomatedBankReconciliation } = useLookupsStore()

  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [matchQueue, setMatchQueue] = useState([])
  const [unclearedPostings, setUnclearedPostings] = useState([])
  const [reMatchTransactionId, setReMatchTransactionId] = useState(null)
  const [reMatchLedgerPostingId, setReMatchLedgerPostingId] = useState(null)

  const fetchMatchQueue = useCallback(async () => {
    const rows = await ospreyAPI({
      service: 'banking',
      endpoint: '/PlaidBankAccount/GetMatchQueue',
      method: 'GET',
      data: { plaidBankAccountId }
    })
    setMatchQueue(Array.isArray(rows) ? rows : [])
  }, [plaidBankAccountId])

  const fetchUnclearedPostings = useCallback(async () => {
    const postings = await ospreyAPI({
      service: 'banking',
      endpoint: '/PlaidBankAccount/GetUnclearedPostings',
      method: 'GET',
      data: { plaidBankAccountId }
    })
    setUnclearedPostings(Array.isArray(postings) ? postings : [])
  }, [plaidBankAccountId])

  useEffect(() => {
    if (!plaidBankAccountId || !enableBankFeeds || !enableAutomatedBankReconciliation) return

    const loadQueueAndPostings = async () => {
      setLoading(true)
      setLoadError(false)
      try {
        await Promise.all([fetchMatchQueue(), fetchUnclearedPostings()])
      } catch (error) {
        console.error('Error loading bank feed review queue:', error)
        setLoadError(true)
      } finally {
        setLoading(false)
      }
    }

    loadQueueAndPostings()
  }, [plaidBankAccountId, enableBankFeeds, enableAutomatedBankReconciliation, fetchMatchQueue, fetchUnclearedPostings])

  const handleConfirm = async (plaidTransactionId) => {
    try {
      await ospreyAPI({
        service: 'banking',
        endpoint: '/PlaidBankAccount/ConfirmMatch',
        method: 'POST',
        sendAsUrlParams: true,
        data: { plaidTransactionId }
      })
      await fetchMatchQueue()
    } catch (error) {
      console.error('Error confirming match:', error)
    }
  }

  const handleIgnore = async (plaidTransactionId) => {
    const proceed = await confirm('Are you sure you want to ignore this transaction? This cannot be undone.', {
      delete: true
    })

    if (!proceed) return

    try {
      await ospreyAPI({
        service: 'banking',
        endpoint: '/PlaidBankAccount/Ignore',
        method: 'POST',
        sendAsUrlParams: true,
        data: { plaidTransactionId }
      })
      await fetchMatchQueue()
    } catch (error) {
      console.error('Error ignoring transaction:', error)
    }
  }

  const openReMatchDialog = (plaidTransactionId) => {
    setReMatchTransactionId(plaidTransactionId)
    setReMatchLedgerPostingId(null)
  }

  const closeReMatchDialog = () => {
    setReMatchTransactionId(null)
    setReMatchLedgerPostingId(null)
  }

  const handleReMatch = async () => {
    if (!reMatchTransactionId || !reMatchLedgerPostingId) return

    try {
      await ospreyAPI({
        service: 'banking',
        endpoint: '/PlaidBankAccount/ReMatch',
        method: 'POST',
        sendAsUrlParams: true,
        data: { plaidTransactionId: reMatchTransactionId, ledgerPostingId: reMatchLedgerPostingId }
      })
      closeReMatchDialog()
      await Promise.all([fetchMatchQueue(), fetchUnclearedPostings()])
    } catch (error) {
      console.error('Error re-matching transaction:', error)
    }
  }

  const columns = [
    {
      field: 'transactionDate',
      headerName: 'Date',
      width: 130,
      renderCell: (params) => <span>{formatDate(params.value)}</span>
    },
    {
      field: 'name',
      headerName: 'Transaction reference',
      flex: 1,
      minWidth: 180
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => <span>{formatCurrency(params.value)}</span>
    },
    {
      field: 'matchStatus',
      headerName: 'Status',
      width: 140
    },
    {
      field: 'candidatePostings',
      headerName: 'Candidate posting(s)',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => {
        const postings = Array.isArray(params.value) ? params.value : []
        if (postings.length === 0) return <span>—</span>
        return <span>{postings.map((posting) => `${posting.postingReference} (${formatCurrency(posting.amount)})`).join(', ')}</span>
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 280,
      sortable: false,
      renderCell: (params) => {
        const row = params.row
        return (
          <MUIStack direction="row" spacing={1}>
            {canConfirmMatch(row.matchStatus) && (
              <MUIButton size="small" variant="outlined" onClick={() => handleConfirm(row.plaidTransactionId)}>
                Confirm
              </MUIButton>
            )}
            {canReMatch(row.matchStatus) && (
              <MUIButton size="small" variant="outlined" onClick={() => openReMatchDialog(row.plaidTransactionId)}>
                Re-match
              </MUIButton>
            )}
            <MUIButton size="small" variant="text" color="error" onClick={() => handleIgnore(row.plaidTransactionId)}>
              Ignore
            </MUIButton>
          </MUIStack>
        )
      }
    }
  ]

  if (!enableBankFeeds || !enableAutomatedBankReconciliation) {
    return <MUIStack data-testid="BankFeedsReviewQueue"></MUIStack>
  }

  return (
    <MUIStack data-testid="BankFeedsReviewQueue" direction="column" spacing={2} sx={{ width: '100%' }}>
      {loadError && (
        <MUIAlert severity="error" sx={{ m: '0 4rem', width: 'calc(100% - 8rem)' }}>
          Unable to load the review queue. Please try again, or contact support if this continues.
        </MUIAlert>
      )}
      <MUIPaper sx={{ height: 'auto', width: 'calc(100% - 8rem)', m: '0 4rem 4rem !important', p: '1.5rem' }}>
        <DataGridPro
          autoHeight
          columns={columns}
          disableColumnMenu
          disableRowSelectionOnClick
          disableVirtualization
          getRowId={(row) => row.plaidTransactionId}
          loading={loading}
          rows={matchQueue}
          sx={{
            [`.MuiDataGrid-root`]: { border: 'none' },
            [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
            [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: { outline: 'none' }
          }}
        />
      </MUIPaper>

      <MUIDialog open={Boolean(reMatchTransactionId)} onClose={closeReMatchDialog} fullWidth maxWidth="sm">
        <MUIDialogTitle>Re-match transaction</MUIDialogTitle>
        <MUIDialogContent>
          <MUIRadioGroup
            value={reMatchLedgerPostingId ?? ''}
            onChange={(event) => setReMatchLedgerPostingId(Number(event.target.value))}
          >
            {unclearedPostings.map((posting) => (
              <MUIFormControlLabel
                key={posting.postingId}
                value={posting.postingId}
                control={<MUIRadio />}
                label={`${posting.postingReference} — ${formatDate(posting.postingDate)} — ${formatCurrency(posting.amount)}`}
              />
            ))}
          </MUIRadioGroup>
        </MUIDialogContent>
        <MUIDialogActions>
          <MUIButton onClick={closeReMatchDialog}>Cancel</MUIButton>
          <MUIButton variant="contained" disabled={!reMatchLedgerPostingId} onClick={handleReMatch}>
            Confirm re-match
          </MUIButton>
        </MUIDialogActions>
      </MUIDialog>
    </MUIStack>
  )
}

export default BankFeedsReviewQueue
